import { useParams, useNavigate } from "react-router-dom";
import "../CSS/ProjectPage.css";
import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import ProjectPlaceholder from "../images/MoviePoster.jpg";
import { BASE_API_URL } from "../constants";

// safe JSON parse (your existing helper)
const safeJsonParse = (str) => {
  if (!str || typeof str !== "string") return null;
  try {
    const cleanedStr = str.replace(/\\"/g, '"');
    return JSON.parse(cleanedStr);
  } catch {
    return null;
  }
};

const cleanText = (value) =>
  value == null ? "" : String(value).replace(/^"|"$/g, "").trim();

const toList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string") return [value].filter(Boolean);

  const parsed = safeJsonParse(value);
  if (Array.isArray(parsed)) return parsed.filter(Boolean);

  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};


const getYouTubeEmbedUrl = (inputUrl) => {
  if (!inputUrl || typeof inputUrl !== "string") return null;

  // if already an embed url, normalize and return
  if (/youtube\.com\/embed\/([A-Za-z0-9_-]+)/.test(inputUrl)) {
    try {
      const u = new URL(inputUrl);
      return u.toString();
    } catch {
      return inputUrl;
    }
  }

  try {
    const url = new URL(inputUrl);
    const host = url.hostname.replace("www.", "");
    // youtu.be short link
    if (host === "youtu.be") {
      // pathname: /VIDEOID
      const id = url.pathname.replace(/^\//, "");
      const params = new URLSearchParams(url.search);
      // preserve other params (like si)
      const qs = params.toString();
      return `https://www.youtube.com/embed/${id}${qs ? "?" + qs : ""}`;
    }

    // youtube full link
    // youtube full link
    if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube.co") {
      // Handle YouTube Shorts (e.g. /shorts/VIDEOID)
      if (url.pathname.startsWith("/shorts/")) {
        const id = url.pathname.replace("/shorts/", "");
        return `https://www.youtube.com/embed/${id}`;
      }

      // watch?v=VIDEOID
      const params = new URLSearchParams(url.search);
      const vid = params.get("v");
      if (vid) {
        // remove v param and keep others
        params.delete("v");
        const qs = params.toString();
        return `https://www.youtube.com/embed/${vid}${qs ? "?" + qs : ""}`;
      }
    }


    // also accept URLs which include 'youtube' somewhere
    if (inputUrl.includes("youtube")) {
      // fallback regex extraction
      const match = inputUrl.match(/(?:v=|\/embed\/|\/)([A-Za-z0-9_-]{6,})/);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return null;
  } catch {
    // input might be raw iframe HTML or something else
    return null;
  }
};

/**
 * Detect if the input is raw HTML containing an iframe
 */
const isIframeHtml = (str) => {
  if (!str || typeof str !== "string") return false;
  return /<iframe[\s\S]*?>[\s\S]*?<\/iframe>/i.test(str) || /<iframe[\s\S]*?\/>/i.test(str);
};

/**
 * Detect if a URL is a direct video file
 */
const isDirectVideoFile = (url) => {
  if (!url || typeof url !== "string") return false;
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
};

/**
 * Build the canonical public project URL — mirrors Next.js route: /project/{id}/{title-with-hyphens}
 * Shared links always open on cinevestor.in (the public website), NOT the contributor panel.
 */
const buildProjectShareUrl = (id, title) => {
  const slug = (title || "")
    .trim()
    .replace(/\s+/g, "-");
  return `https://cinevestor.in/project/${id}/${slug}`;
};

/**
 * Build share description — mirrors Next.js generateMetadata() logic:
 * strips HTML tags, uses whySupport → synopsis → briefStory, truncated to 160 chars.
 */
const buildShareDescription = (project) => {
  const raw = project.whySupport || project.synopsis || project.briefStory || "Support this film project on Cinevestor";
  const stripped = String(raw).replace(/<[^>]*>?/gm, "");
  const truncated = stripped.substring(0, 160);
  return `${truncated}\n\nCinevestor is Crowd contribution platform where you contribute financially on listed movie project.`;
};

/* ---------------------------
   Main Component
   --------------------------- */

const ProjectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("synopsis"); // default tab changed to synopsis
  const [referralData, setReferralData] = useState(null);

  // Lightbox State
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetch(`${BASE_API_URL}/api/projects/${id}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(`HTTP ${res.status}`)))
      .then((data) => {
        setProject(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId || !project?.ProjectID) return;

    fetch(`${BASE_API_URL}/api/referral/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: Number(userId), projectId: project.ProjectID }),
    })
      .then((res) => res.json())
      .then((data) => setReferralData(data))
      .catch((err) => console.error("Referral link generation failed:", err));
  }, [project]);

  if (loading) return <div className="loading-message">Loading project details...</div>;
  if (!project) return <div className="error-message">Project not found.</div>;

  const projectPosters = Array.isArray(project.posters)
    ? project.posters
    : safeJsonParse(project.posters) || [];
  const galleryImages = toList(project.gallery).length
    ? toList(project.gallery)
    : projectPosters;
  const teamMembers = Array.isArray(project.team)
    ? project.team
    : safeJsonParse(project.team) || [];
  const supportNeeded = toList(project.supportNeeded);
  const statusFeatures = toList(project.statusFeatures);

  const getPosterUrl = (posterPath) => {
    if (!posterPath) return ProjectPlaceholder;
    if (typeof posterPath !== "string") return ProjectPlaceholder;
    if (posterPath.startsWith("http")) return posterPath;
    if (posterPath.startsWith("/")) return `${BASE_API_URL}${posterPath}`;
    return `${BASE_API_URL}/images/${posterPath}`;
  };

  const getTeamPhotoUrl = (photoPath) =>
    photoPath && !photoPath.startsWith("http")
      ? getPosterUrl(photoPath)
      : photoPath || ProjectPlaceholder;

  // ── Social Sharing Data (URL always points to cinevestor.in public site) ──────
  const shareUrl = referralData?.shareUrl || buildProjectShareUrl(project.ProjectID || id, project.title);
  const shareTitle = project.title
    ? `Cinevestor Exclusive Project: ${project.title}`
    : "Cinevestor Exclusive Project";
  const shareDescription = buildShareDescription(project);
  // shareImage is used by OG tags on cinevestor.in — set correctly for completeness
  const shareImage = getPosterUrl(projectPosters[0]);

  const trackShare = () => {
    if (referralData?.referralCode) {
      fetch(`${BASE_API_URL}/api/referral/share-count/${referralData.referralCode}`, {
        method: "POST",
      }).catch(() => { });
    }
  };

  const handleFacebookShare = () => {
    trackShare();
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const handleWhatsAppShare = () => {
    trackShare();
    const text = `${shareTitle}\n\n${shareDescription}\n\n${shareUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTwitterShare = () => {
    trackShare();
    const shortDesc = cleanText(project.whySupport || project.synopsis || project.briefStory || "")
      .replace(/<[^>]*>?/gm, "")
      .substring(0, 100); // shorter — leaves room for title + URL
    const text = `${shareTitle} — ${shortDesc}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const handleLinkedInShare = () => {
    trackShare();
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const handleNativeShare = async () => {
    trackShare();
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Share failed:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch {
        console.error("Clipboard copy failed");
      }
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const currentFunding = project.AmountRaised || 0;
  const fundingGoal = project.fundingGoal || project.fundingal || 0;
  const minContribution = project.minContribution || "N/A";
  const progress =
    fundingGoal > 0 ? Math.min((currentFunding / fundingGoal) * 100, 100) : 0;

  const currencyFormatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });

  const formattedFundingGoal =
    fundingGoal > 0 ? currencyFormatter.format(fundingGoal) : "N/A";
  const formattedMinContribution =
    minContribution !== "N/A" ? currencyFormatter.format(minContribution) : "N/A";

  // === VIDEO SOURCE (could be a URL, an iframe HTML string, or embedded html) ===
  const videoSource = project.videoLink; // may be "https://youtu.be/IdQPdJ4T6_I?si=..." or raw iframe HTML or mp4 link
  const youtubeEmbed = getYouTubeEmbedUrl(videoSource);
  const iframeHtml = isIframeHtml(videoSource);
  const isDirectVideo = isDirectVideoFile(videoSource);

  // Lightbox Handlers
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => setIsLightboxOpen(false);
  const goToNextImage = () => setCurrentImageIndex((prev) => (prev + 1) % projectPosters.length);
  const goToPrevImage = () => setCurrentImageIndex((prev) => (prev - 1 + projectPosters.length) % projectPosters.length);

  const renderTabContent = () => {
    switch (activeTab) {
      case "synopsis":
        return <p style={{ fontSize: "0.95rem" }}>{cleanText(project.synopsis || project.briefStory) || "No synopsis available."}</p>;
      case "about":
        return <p style={{ fontSize: "0.95rem" }}>{cleanText(project.aboutProject) || "No detailed description provided."}</p>;
      case "whysupport":
        return <p style={{ fontSize: "0.95rem" }}>{cleanText(project.whySupport) || "No reason provided."}</p>;
      case "finance":
        return (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", fontSize: "0.95rem" }}>
            <div><strong>Funding Goal:</strong> {formattedFundingGoal}</div>
            <div><strong>Min Contribution:</strong> {formattedMinContribution}</div>
            <div><strong>Profit Sharing:</strong> {project.profitPlan ? `${project.profitPlan}%` : "Pending details"}</div>
            <div><strong>Utilization:</strong> {cleanText(project.utilization) || "N/A"}</div>
            <div><strong>Recovery Plan:</strong> {cleanText(project.recoveryPlan) || "N/A"}</div>
          </div>
        );
      case "team":
        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "20px" }}>
            {teamMembers.length > 0 ? teamMembers.map((member, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <img src={getTeamPhotoUrl(member.photo || member.image || member.photoUrl)} alt={member.name || "Team Member"} style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover", marginBottom: "10px" }} onError={(e) => e.target.src = ProjectPlaceholder} />
                <h4 style={{ margin: "5px 0", color: "#e53170", fontSize: "1rem" }}>{member.name || member.memberName || member.title || "Team Member"}</h4>
                <p style={{ fontSize: "0.85rem", color: "#ccc", margin: 0 }}>{member.role || member.designation || "Crew"}</p>
              </div>
            )) : <p>Team details coming soon.</p>}
          </div>
        );
      case "video":
        return (
          <div style={{ textAlign: "center" }}>
            {youtubeEmbed ? (
              <iframe width="100%" height="400" src={youtubeEmbed} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            ) : isDirectVideo ? (
              <video width="100%" height="400" controls controlsList="nodownload"><source src={videoSource} type="video/mp4" />Your browser does not support the video tag.</video>
            ) : iframeHtml ? (
              <div dangerouslySetInnerHTML={{ __html: iframeHtml }} />
            ) : (
              <p>No video available.</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: "#0f0e17", minHeight: "100vh", color: "white", paddingBottom: "50px", fontFamily: "'Poppins', sans-serif" }}>
      {/* Back Button Overlay */}
      <button
        onClick={() => navigate(-1)}
        style={{ position: "absolute", top: "20px", left: "20px", background: "rgba(0,0,0,0.5)", color: "white", border: "none", padding: "10px 15px", borderRadius: "8px", cursor: "pointer", zIndex: 10, display: "flex", alignItems: "center", gap: "8px", fontWeight: "bold" }}
      >
        <FaArrowLeft /> Back
      </button>

      {/* Contribute Now — top-right corner of hero */}
      <button
        onClick={() => navigate("/contribution", { state: { selectedProjectId: id } })}
        className="hero-contribute-btn"
      >
        Contribute Now
      </button>

      {/* Hero Section */}
      <section style={{
        position: "relative",
        backgroundImage: `linear-gradient(to right, rgba(15, 14, 23, 1) 0%, rgba(15, 14, 23, 0.8) 50%, rgba(15, 14, 23, 0.4) 100%), url(${getPosterUrl(projectPosters[0])})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "120px 80px",
        minHeight: "500px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <div style={{ maxWidth: "800px", zIndex: 1 }}>
          <h1 style={{ color: "#e53170", fontSize: "3rem", marginBottom: "15px", fontWeight: "bold", textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
            <span style={{ color: "#ffffff", fontWeight: "600" }}>Cinevestor Exclusive Project: </span>
            {project.title || "Untitled Project"}
          </h1>
          <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{ background: "#d946ef", color: "white", padding: "6px 20px", borderRadius: "8px", fontSize: "0.85rem", fontWeight: "bold" }}>
              {project.stage || "Development"}
            </span>
            <span style={{ color: "#d1d5db", fontSize: "0.95rem", textShadow: "0 1px 5px rgba(0,0,0,0.8)" }}>
              {project.type || "Movies"} ({project.genreCategory || project.genres || "Genre N/A"})
            </span>
          </div>

          <div style={{ width: "100%", maxWidth: "400px", marginTop: "30px" }}>
            <div style={{ background: "rgba(255,255,255,0.2)", height: "8px", borderRadius: "4px", marginBottom: "10px" }}>
              <div style={{ background: "#e53170", height: "100%", width: `${progress}%`, borderRadius: "4px", transition: "width 1s ease", boxShadow: "0 0 10px rgba(229, 49, 112, 0.8)" }}></div>
            </div>
            <p style={{ fontSize: "0.9rem", color: "#ffffff", fontWeight: "600", margin: 0, textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
              {Math.round(progress)}% Funded &nbsp;&nbsp;|&nbsp;&nbsp; Goal: {formattedFundingGoal}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section style={{ padding: "50px 20px", maxWidth: "1200px", margin: "0 auto" }}>

        {/* Social Share Section — above Project Posters */}
        <div className="share-section">
          <h3 className="share-title">Share This Project</h3>
          <div className="share-icons-row">
            <button className="share-icon-btn facebook" onClick={handleFacebookShare} aria-label="Share on Facebook" title="Share on Facebook">
              <i className="fab fa-facebook-f"></i>
            </button>
            <button className="share-icon-btn whatsapp" onClick={handleWhatsAppShare} aria-label="Share on WhatsApp" title="Share on WhatsApp">
              <i className="fab fa-whatsapp"></i>
            </button>
            <button className="share-icon-btn twitter" onClick={handleTwitterShare} aria-label="Share on X (Twitter)" title="Share on X (Twitter)">
              <i className="fa-brands fa-x-twitter"></i>
            </button>
            <button className="share-icon-btn linkedin" onClick={handleLinkedInShare} aria-label="Share on LinkedIn" title="Share on LinkedIn">
              <i className="fab fa-linkedin-in"></i>
            </button>
            <button className="share-icon-btn native" onClick={handleNativeShare} aria-label="More sharing options" title="More sharing options">
              <i className="fas fa-share-alt"></i>
            </button>
          </div>
          {copySuccess && (
            <p className="share-copy-toast">Link copied to clipboard!</p>
          )}
        </div>

        {/* Project Posters */}
        <h2 style={{ color: "#e53170", textAlign: "center", marginBottom: "30px", fontSize: "1.5rem", fontWeight: "bold" }}>Project Posters</h2>
        {projectPosters.length > 0 ? (
          <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginBottom: "50px", flexWrap: "wrap" }}>
            {projectPosters.slice(0, 2).map((poster, i) => (
              <div
                key={i}
                onClick={() => openLightbox(i === 1 && projectPosters.length > 2 ? 1 : i)}
                style={{
                  position: "relative",
                  height: "250px",
                  display: "inline-block",
                  borderRadius: "12px",
                  overflow: "hidden",
                  cursor: "pointer",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.5)"
                }}
              >
                <img src={getPosterUrl(poster)} alt={`Poster ${i}`} style={{ height: "100%", width: "auto", display: "block", objectFit: "contain" }} onError={(e) => e.target.src = ProjectPlaceholder} />
                {i === 1 && projectPosters.length > 2 && (
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "2.5rem", fontWeight: "bold" }}>
                    +{projectPosters.length - 2}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ textAlign: "center", color: "#ccc", marginBottom: "50px" }}>No posters available.</p>
        )}

        {/* Tabs Nav */}
        <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "30px", flexWrap: "wrap" }}>
          {["Synopsis", "About", "WhySupport", "Finance", "Team", "Video"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab.toLowerCase())}
              style={{
                background: activeTab === tab.toLowerCase() ? "#a855f7" : "transparent",
                border: activeTab === tab.toLowerCase() ? "none" : "1px solid #4c1d95",
                color: "white",
                padding: "8px 25px",
                borderRadius: "20px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "0.9rem",
                transition: "all 0.3s ease"
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content Box */}
        <div style={{
          background: "rgba(30, 27, 56, 0.6)",
          padding: "40px",
          borderRadius: "15px",
          color: "#d1d5db",
          maxWidth: "1000px",
          margin: "0 auto 50px auto",
          lineHeight: "1.8",
          boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
          minHeight: "150px"
        }}>
          {renderTabContent()}
        </div>

        {/* Bottom Contribute Button (kept for completeness at page end) */}
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button
            onClick={() => navigate("/contribution", { state: { selectedProjectId: id } })}
            style={{
              background: "linear-gradient(90deg, #ec4899, #3b82f6)",
              color: "white",
              border: "none",
              padding: "15px 50px",
              borderRadius: "30px",
              fontSize: "1.1rem",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 4px 15px rgba(236, 72, 153, 0.4)",
              transition: "transform 0.3s ease"
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.05)"}
            onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
          >
            Contribute Now
          </button>
        </div>
      </section>

      {/* Lightbox Modal */}
      {isLightboxOpen && projectPosters.length > 0 && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
            <button className="lightbox-prev" onClick={goToPrevImage}>&#10094;</button>
            <img
              src={getPosterUrl(projectPosters[currentImageIndex])}
              alt={`Expanded Poster ${currentImageIndex + 1}`}
              className="lightbox-image"
            />
            <button className="lightbox-next" onClick={goToNextImage}>&#10095;</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPage;
